import React, { createContext, useContext } from 'react';
import type { CaptainMessage, OngoingOrderSnapshot } from '../types/events';
import type { AiChatMessage } from '../../hooks/useAiCaptainSession';
import type { AiCaptainMenuItem } from '../../utils/aiCaptainMenuItems';

export interface VirtualCaptainContextValue {
  /** True when widget should render (all authenticated customers — no feature gate) */
  active: boolean;
  /** True when restaurant has quick_assistance feature — shows service-request buttons */
  assistanceEnabled: boolean;
  /** True when dine-in table has been allocated to this user (pre-order) */
  tableAllocated: boolean;
  /** Allocated table number for pre-order display (when tableAllocated is true and no order yet) */
  allocatedTableNumber?: number;
  restaurantName: string;
  messages: CaptainMessage[];
  latestMessage: CaptainMessage | null;
  ongoingOrder: OngoingOrderSnapshot | null;
  voiceEnabled: boolean;
  setVoiceEnabled: (enabled: boolean) => void;
  /** Call on user tap to unlock speech on Safari / mobile browsers */
  unlockVoice: () => void;
  showVoiceTip: boolean;
  setShowVoiceTip: (show: boolean) => void;
  dismissVoiceTip?: () => void;
  enableVoiceFromTip?: () => void;
  pushMessage: (message: CaptainMessage, speakText?: string) => void;
  submitServiceRequest: (
    service: string,
    label: string,
    order?: OngoingOrderSnapshot
  ) => Promise<boolean>;
  callCaptain: (order?: OngoingOrderSnapshot) => Promise<boolean>;
  assistanceAvailable: boolean;
  /** Peppy AI conversational chat (optional — enabled when ai-captain-service is connected) */
  chatEnabled?: boolean;
  chatMessages?: AiChatMessage[];
  streamingText?: string;
  streamingMenuItems?: import('../../utils/aiCaptainMenuItems').AiCaptainMenuItem[];
  streamingTableItems?: import('../../utils/aiCaptainTables').AiCaptainTableItem[];
  streamingDealItems?: import('../../utils/aiCaptainDeals').AiCaptainDealItem[];
  chatConnected?: boolean;
  chatConnectionFailed?: boolean;
  sendChatMessage?: (text: string) => void;
  submitChoiceSelection?: (messageId: string, values: string[]) => void;
  addMenuItemToCart?: (
    item: AiCaptainMenuItem,
    orderType?: number
  ) => Promise<{ ok: boolean; message?: string }>;
  appName?: string | null;
  restaurantId?: number;
}

const VirtualCaptainContext = createContext<VirtualCaptainContextValue | null>(null);

export const VirtualCaptainProvider = VirtualCaptainContext.Provider;

export function useVirtualCaptain(): VirtualCaptainContextValue {
  const ctx = useContext(VirtualCaptainContext);
  if (!ctx) {
    throw new Error('useVirtualCaptain must be used within VirtualCaptainProvider');
  }
  return ctx;
}

export function useOptionalVirtualCaptain(): VirtualCaptainContextValue | null {
  return useContext(VirtualCaptainContext);
}
